import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Slider } from "../components/ui/slider";
import RehabCalculator from "../components/RehabCalculator";
import DealCommandCenter from "../components/DealCommandCenter";
import ExecutionCockpitHeader from "../components/ExecutionCockpitHeader";
import AIAutopilotPanel from "../components/AIAutopilotPanel";
import WorkflowActions from "../components/WorkflowActions";
import EscrowPanel from "../components/EscrowPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import { 
  ArrowLeft, Home, User, Calculator, MessageSquare, Image, Save, Plus, Trash2, Upload, FileText, Phone, Mail, MapPin, Crown, X, Lock, Unlock, AlertTriangle, CheckCircle, Info, TrendingUp, DollarSign, Percent, Calendar, Link as LinkIcon, FolderOpen, Target, HelpCircle, ArrowRight, Zap, Circle, ChevronRight, Building2, BarChart3, Loader2, Store, Pause, ExternalLink, Banknote, Clock, Users, Star, Brain
} from "lucide-react";
import LendersTab from "../components/LendersTab";
import DealMemoGenerator from "../components/DealMemoGenerator";
import SmartTemplatesPanel from "../components/SmartTemplatesPanel";
import PropertyIntelligence, { CompsPanel } from "../components/PropertyIntelligence";
import DealNetworkPanel from "../components/DealNetworkPanel";
import DealTimelineTab from "../components/DealTimelineTab";
import PostCloseReviewModal from "../components/PostCloseReviewModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============== AMORTIZATION HELPERS ==============
const calculateMonthlyPayment = (principal, annualRate, termYears) => {
  if (principal <= 0 || termYears <= 0) return 0;
  if (annualRate <= 0) return principal / (termYears * 12);
  
  const monthlyRate = annualRate / 100 / 12;
  const numPayments = termYears * 12;
  
  const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
  return Math.round(payment * 100) / 100;
};

const calculateRemainingBalance = (principal, annualRate, termYears, paymentsMade) => {
  if (principal <= 0 || termYears <= 0) return 0;
  if (paymentsMade <= 0) return principal;
  
  if (annualRate <= 0) {
    const monthlyPayment = principal / (termYears * 12);
    return Math.max(0, principal - (monthlyPayment * paymentsMade));
  }
  
  const monthlyRate = annualRate / 100 / 12;
  const numPayments = termYears * 12;
  
  if (paymentsMade >= numPayments) return 0;
  
  const monthlyPayment = calculateMonthlyPayment(principal, annualRate, termYears);
  const remaining = principal * Math.pow(1 + monthlyRate, paymentsMade) - monthlyPayment * ((Math.pow(1 + monthlyRate, paymentsMade) - 1) / monthlyRate);
  return Math.max(0, Math.round(remaining * 100) / 100);
};

export default function DealDetailPage({ user, subscription, onLogout }) {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { mainClass } = useSidebar();
  const [deal, setDeal] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [activities, setActivities] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("property");
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReviewNudge, setShowReviewNudge] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [calcPreview, setCalcPreview] = useState(null);
  const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(true);
  // Deal Package
  const [generatingPackage, setGeneratingPackage] = useState(false);
  const [packageShareUrl, setPackageShareUrl] = useState(null);
  const [fundingRequests, setFundingRequests] = useState([]);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [fundingForm, setFundingForm] = useState({
    loan_amount_requested: "",
    loan_type: "hard_money",
    timeline_days: 30,
    notes: ""
  });
  const [submittingFunding, setSubmittingFunding] = useState(false);
  
  // Comparables state
  const [showCompModal, setShowCompModal] = useState(false);
  const [compForm, setCompForm] = useState({
    address: "",
    sale_price: "",
    sale_date: "",
    beds: "",
    baths: "",
    sqft: "",
    distance_miles: "",
    condition: "similar",
    notes: "",
    source_url: ""
  });
  const [savingComp, setSavingComp] = useState(false);
  const [parsingUrl, setParsingUrl] = useState(false);
  const [urlToParse, setUrlToParse] = useState("");
  const [savingMaoPercent, setSavingMaoPercent] = useState(false);
  
  const tier = subscription?.tier || user?.subscription_tier || "starter";

  // Scenario form state with all creative finance fields
  const [scenarioForm, setScenarioForm] = useState({
    name: "",
    strategy: "wholesale",
    // Common
    arv: "",
    purchase_price: "",
    rehab_estimate: "0",
    closing_costs: "0",
    holding_costs: "0",
    desired_profit: "",
    // Subject-To
    existing_loan_balance: "",
    existing_interest_rate: "",
    remaining_term_years: "",
    monthly_piti: "",
    arrears: "0",
    cash_to_seller: "0",
    holding_period_months: "12",
    exit_strategy: "keep_rental",
    monthly_rent: "",
    // Seller Finance
    down_payment: "",
    interest_rate: "",
    amortization_years: "30",
    balloon_years: "",
    exit_price: "",
    // Wrap
    underlying_balance: "",
    underlying_rate: "",
    underlying_term_years: "",
    underlying_monthly_piti: "",
    wrap_sale_price: "",
    wrap_down_payment: "",
    wrap_interest_rate: "",
    wrap_amortization_years: "30",
    wrap_balloon_years: "",
    // Wholesale
    assignment_fee: ""
  });

  const [noteForm, setNoteForm] = useState({
    content: "",
    contact_outcome: "",
    note_type: "general"
  });

  const [closeForm, setCloseForm] = useState({
    status: "closed",
    reason: "",
    // Closed deal metrics
    actual_purchase_price: "",
    actual_sale_price: "",
    actual_rehab_cost: "",
    closing_date: new Date().toISOString().split('T')[0],
    buyer_name: "",
    buyer_type: "investor", // investor, owner_occupant, wholesaler
    assignment_fee: "",
    closing_costs: "",
    holding_costs: "",
    notes: ""
  });

  const [newLink, setNewLink] = useState({ name: "", url: "" });

  const strategies = [
    { value: "cash", label: "Cash" },
    { value: "wholesale", label: "Wholesale" },
    { value: "fix_flip", label: "Fix & Flip" },
    { value: "subject_to", label: "Subject-To" },
    { value: "seller_finance", label: "Seller Finance" },
    { value: "wrap", label: "Wrap" }
  ];

  const stages = [
    // Acquisition Phase
    { value: "new_lead", label: "New Lead", phase: "acquisition" },
    { value: "analyzed", label: "Analyzed", phase: "acquisition" },
    { value: "underwritten", label: "Underwritten", phase: "acquisition" },
    { value: "contracted", label: "Contracted", phase: "acquisition" },
    // Processing Phase
    { value: "rehab_estimate", label: "Rehab Estimate", phase: "processing" },
    { value: "sent_to_title", label: "Sent to Title", phase: "processing" },
    { value: "title_processing", label: "Title Processing", phase: "processing" },
    { value: "ready_to_close", label: "Ready to Close", phase: "processing" },
    // Closing Phase
    { value: "closed", label: "Closed", phase: "closed" },
    // Disposition Phase
    { value: "posting_contractors", label: "Posting for Contractors", phase: "disposition" },
    { value: "contractor_selected", label: "Contractor Selected", phase: "disposition" },
    { value: "finding_realtor", label: "Finding Realtor", phase: "disposition" },
    { value: "listed", label: "Listed", phase: "disposition" },
    { value: "under_contract_sale", label: "Under Contract (Sale)", phase: "disposition" },
    { value: "sold", label: "Sold", phase: "final" },
    // Terminal
    { value: "dead", label: "Dead", phase: "terminal" }
  ];

  const closeReasons = [
    { value: "numbers_didnt_work", label: "Numbers didn't work" },
    { value: "risk_too_high", label: "Risk too high" },
    { value: "seller_unrealistic", label: "Seller unrealistic" },
    { value: "buyer_issue", label: "Buyer issue" },
    { value: "timing", label: "Timing" },
    { value: "deal_closed", label: "Deal closed successfully" },
    { value: "other", label: "Other" }
  ];

  const contactOutcomes = [
    { value: "spoke", label: "Spoke" },
    { value: "vm", label: "Voicemail" },
    { value: "no_answer", label: "No Answer" },
    { value: "follow_up", label: "Follow-Up" }
  ];

  const noteTypes = [
    { value: "general", label: "General Note" },
    { value: "offer", label: "Offer Notes" },
    { value: "call_summary", label: "Call Summary" }
  ];

  useEffect(() => {
    fetchDealData();
    fetchScripts();
  }, [dealId]);

  // Real-time calculation preview
  useEffect(() => {
    if (showScenarioModal && scenarioForm.strategy) {
      calculatePreview();
    }
  }, [scenarioForm, showScenarioModal]);

  const fetchDealData = async () => {
    try {
      const [dealRes, scenariosRes, activityRes, photosRes, filesRes, guidanceRes, fundingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/deals/${dealId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/deals/${dealId}/scenarios`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/deals/${dealId}/activity`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/deals/${dealId}/photos`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/deals/${dealId}/files`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/deals/${dealId}/guidance`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/deals/${dealId}/funding-requests`, { withCredentials: true }).catch(() => ({ data: { funding_requests: [] } }))
      ]);
      
      setDeal(dealRes.data);
      setScenarios(scenariosRes.data);
      setActivities(activityRes.data);
      setPhotos(photosRes.data);
      setFiles(filesRes.data);
      setGuidance(guidanceRes.data);
      setFundingRequests(fundingRes.data?.funding_requests || []);
    } catch (error) {
      console.error("Failed to fetch deal:", error);
      toast.error("Failed to load deal");
      navigate("/deals");
    } finally {
      setLoading(false);
    }
  };

  const fetchScripts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/scripts`, { withCredentials: true });
      setScripts(response.data);
    } catch (error) {
      console.error("Failed to fetch scripts:", error);
    }
  };

  // Helper function to advance deal stage
  const generateDealPackage = async () => {
    setGeneratingPackage(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/deals/${deal.deal_id}/generate-package`, {}, { withCredentials: true });
      setPackageShareUrl(res.data.share_url);
      toast.success("Deal package created — link ready to share!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Package generation failed");
    } finally {
      setGeneratingPackage(false);
    }
  };

  const advanceDealStage = async () => {    const stageOrder = [
      "new_lead", "analyzed", "underwritten", "contracted",
      "rehab_estimate", "sent_to_title", "title_processing", "ready_to_close",
      "closed", "posting_contractors", "contractor_selected", "finding_realtor",
      "listed", "under_contract_sale", "sold"
    ];
    const currentIndex = stageOrder.indexOf(deal.pipeline_stage);
    if (currentIndex >= 0 && currentIndex < stageOrder.length - 1) {
      const nextStage = stageOrder[currentIndex + 1];
      try {
        await axios.put(
          `${BACKEND_URL}/api/deals/${deal.deal_id}`,
          { pipeline_stage: nextStage },
          { withCredentials: true }
        );
        setDeal(prev => ({ ...prev, pipeline_stage: nextStage }));
        toast.success(`Stage advanced to ${nextStage.replace(/_/g, ' ')}`);
        // Refresh guidance for new stage
        const guidanceRes = await axios.get(
          `${BACKEND_URL}/api/deals/${deal.deal_id}/guidance`,
          { withCredentials: true }
        );
        setGuidance(guidanceRes.data);
        return true;
      } catch (error) {
        toast.error("Failed to advance stage");
        return false;
      }
    }
    return false;
  };

  const calculatePreview = async () => {
    try {
      const payload = buildScenarioPayload();
      const response = await axios.post(`${BACKEND_URL}/api/calculator/calculate`, payload, { withCredentials: true });
      setCalcPreview(response.data);
    } catch (error) {
      console.error("Calculation error:", error);
    }
  };

  // Submit funding request
  const handleSubmitFundingRequest = async () => {
    if (!fundingForm.loan_amount_requested || parseFloat(fundingForm.loan_amount_requested) <= 0) {
      toast.error("Please enter a valid loan amount");
      return;
    }
    
    setSubmittingFunding(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/deals/${dealId}/funding-request`,
        {
          loan_amount_requested: parseFloat(fundingForm.loan_amount_requested),
          loan_type: fundingForm.loan_type,
          timeline_days: parseInt(fundingForm.timeline_days) || 30,
          notes: fundingForm.notes
        },
        { withCredentials: true }
      );
      
      toast.success("Funding request submitted to lender network!");
      setShowFundingModal(false);
      setFundingForm({ loan_amount_requested: "", loan_type: "hard_money", timeline_days: 30, notes: "" });
      
      // Refresh funding requests
      const fundingRes = await axios.get(`${BACKEND_URL}/api/deals/${dealId}/funding-requests`, { withCredentials: true });
      setFundingRequests(fundingRes.data?.funding_requests || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit funding request");
    } finally {
      setSubmittingFunding(false);
    }
  };

  // Select a lender for funding
  const handleSelectLender = async (requestId, lenderId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/deals/${dealId}/funding-requests/${requestId}/select-lender/${lenderId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Lender selected! They will be notified.");
      
      // Refresh funding requests
      const fundingRes = await axios.get(`${BACKEND_URL}/api/deals/${dealId}/funding-requests`, { withCredentials: true });
      setFundingRequests(fundingRes.data?.funding_requests || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to select lender");
    }
  };

  const buildScenarioPayload = () => {
    const base = {
      deal_id: dealId,
      name: scenarioForm.name || `${scenarioForm.strategy} Analysis`,
      strategy: scenarioForm.strategy
    };

    const parseNum = (val) => parseFloat(val) || 0;

    switch (scenarioForm.strategy) {
      case "cash":
        return { ...base, arv: parseNum(scenarioForm.arv), purchase_price: parseNum(scenarioForm.purchase_price), closing_costs: parseNum(scenarioForm.closing_costs), desired_profit: parseNum(scenarioForm.desired_profit) };
      
      case "wholesale":
        return { ...base, arv: parseNum(scenarioForm.arv), purchase_price: parseNum(scenarioForm.purchase_price), assignment_fee: parseNum(scenarioForm.assignment_fee) || parseNum(scenarioForm.desired_profit), closing_costs: parseNum(scenarioForm.closing_costs) };
      
      case "fix_flip":
        return { ...base, arv: parseNum(scenarioForm.arv), purchase_price: parseNum(scenarioForm.purchase_price), rehab_estimate: parseNum(scenarioForm.rehab_estimate), closing_costs: parseNum(scenarioForm.closing_costs), holding_costs: parseNum(scenarioForm.holding_costs), desired_profit: parseNum(scenarioForm.desired_profit) };
      
      case "subject_to":
        return { ...base, existing_loan_balance: parseNum(scenarioForm.existing_loan_balance), existing_interest_rate: parseNum(scenarioForm.existing_interest_rate), remaining_term_years: parseNum(scenarioForm.remaining_term_years), monthly_piti: parseNum(scenarioForm.monthly_piti), arrears: parseNum(scenarioForm.arrears), cash_to_seller: parseNum(scenarioForm.cash_to_seller), closing_costs: parseNum(scenarioForm.closing_costs), holding_period_months: parseInt(scenarioForm.holding_period_months) || 12, exit_strategy: scenarioForm.exit_strategy, monthly_rent: parseNum(scenarioForm.monthly_rent) };
      
      case "seller_finance":
        return { ...base, purchase_price: parseNum(scenarioForm.purchase_price), down_payment: parseNum(scenarioForm.down_payment), interest_rate: parseNum(scenarioForm.interest_rate), amortization_years: parseInt(scenarioForm.amortization_years) || 30, balloon_years: parseInt(scenarioForm.balloon_years) || 0, closing_costs: parseNum(scenarioForm.closing_costs), holding_period_months: parseInt(scenarioForm.holding_period_months) || 12, monthly_rent: parseNum(scenarioForm.monthly_rent), exit_price: parseNum(scenarioForm.exit_price) };
      
      case "wrap":
        return { ...base, underlying_balance: parseNum(scenarioForm.underlying_balance), underlying_rate: parseNum(scenarioForm.underlying_rate), underlying_term_years: parseNum(scenarioForm.underlying_term_years), underlying_monthly_piti: parseNum(scenarioForm.underlying_monthly_piti), wrap_sale_price: parseNum(scenarioForm.wrap_sale_price), wrap_down_payment: parseNum(scenarioForm.wrap_down_payment), wrap_interest_rate: parseNum(scenarioForm.wrap_interest_rate), wrap_amortization_years: parseInt(scenarioForm.wrap_amortization_years) || 30, wrap_balloon_years: parseInt(scenarioForm.wrap_balloon_years) || 0, closing_costs: parseNum(scenarioForm.closing_costs), holding_period_months: parseInt(scenarioForm.holding_period_months) || 60 };
      
      default:
        return base;
    }
  };

  const handleSaveDeal = async () => {
    if (deal?.is_locked) {
      toast.error("Deal is locked. Unlock to edit.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/deals/${dealId}`, deal, { withCredentials: true });
      toast.success("Deal saved");
      fetchDealData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save deal");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLock = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/deals/${dealId}/lock`, {}, { withCredentials: true });
      setDeal({ ...deal, is_locked: response.data.is_locked });
      toast.success(response.data.is_locked ? "Deal locked" : "Deal unlocked");
    } catch (error) {
      toast.error("Failed to toggle lock");
    }
  };

  const handleCloseDeal = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/deals/${dealId}/close`, closeForm, { withCredentials: true });
      toast.success(`Deal ${closeForm.status === 'closed' ? 'closed' : 'killed'}`);
      setShowCloseModal(false);
      fetchDealData();
      // Trigger the partner review nudge on successful close (won only)
      if (closeForm.status === 'closed') {
        setShowReviewNudge(true);
      }
    } catch (error) {
      toast.error("Failed to close deal");
    }
  };

  const handleCreateScenario = async (e) => {
    e.preventDefault();
    
    try {
      const payload = buildScenarioPayload();

      if (editingScenario) {
        await axios.put(`${BACKEND_URL}/api/scenarios/${editingScenario.scenario_id}`, payload, { withCredentials: true });
        toast.success("Scenario updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/scenarios`, payload, { withCredentials: true });
        toast.success("Scenario created");
      }
      
      setShowScenarioModal(false);
      resetScenarioForm();
      fetchDealData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save scenario");
    }
  };

  const resetScenarioForm = () => {
    setEditingScenario(null);
    setCalcPreview(null);
    setScenarioForm({
      name: "", strategy: "wholesale", arv: "", purchase_price: "", rehab_estimate: deal?.rehab_estimate?.toString() || "0", closing_costs: "0", holding_costs: "0", desired_profit: "",
      existing_loan_balance: "", existing_interest_rate: "", remaining_term_years: "", monthly_piti: "", arrears: "0", cash_to_seller: "0", holding_period_months: "12", exit_strategy: "keep_rental", monthly_rent: "",
      down_payment: "", interest_rate: "", amortization_years: "30", balloon_years: "", exit_price: "",
      underlying_balance: "", underlying_rate: "", underlying_term_years: "", underlying_monthly_piti: "", wrap_sale_price: "", wrap_down_payment: "", wrap_interest_rate: "", wrap_amortization_years: "30", wrap_balloon_years: "",
      assignment_fee: ""
    });
  };

  const handleDeleteScenario = async (scenarioId) => {
    if (!window.confirm("Delete this scenario?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/scenarios/${scenarioId}`, { withCredentials: true });
      toast.success("Scenario deleted");
      fetchDealData();
    } catch (error) {
      toast.error("Failed to delete scenario");
    }
  };

  const handleEditScenario = (scenario) => {
    setEditingScenario(scenario);
    setScenarioForm({
      name: scenario.name || "",
      strategy: scenario.strategy || "wholesale",
      arv: scenario.arv?.toString() || "",
      purchase_price: scenario.purchase_price?.toString() || "",
      rehab_estimate: scenario.rehab_estimate?.toString() || "0",
      closing_costs: scenario.closing_costs?.toString() || "0",
      holding_costs: scenario.holding_costs?.toString() || "0",
      desired_profit: scenario.desired_profit?.toString() || "",
      existing_loan_balance: scenario.existing_loan_balance?.toString() || "",
      existing_interest_rate: scenario.existing_interest_rate?.toString() || "",
      remaining_term_years: scenario.remaining_term_years?.toString() || "",
      monthly_piti: scenario.monthly_piti?.toString() || "",
      arrears: scenario.arrears?.toString() || "0",
      cash_to_seller: scenario.cash_to_seller?.toString() || "0",
      holding_period_months: scenario.holding_period_months?.toString() || "12",
      exit_strategy: scenario.exit_strategy || "keep_rental",
      monthly_rent: scenario.monthly_rent?.toString() || "",
      down_payment: scenario.down_payment?.toString() || "",
      interest_rate: scenario.interest_rate?.toString() || "",
      amortization_years: scenario.amortization_years?.toString() || "30",
      balloon_years: scenario.balloon_years?.toString() || "",
      exit_price: scenario.exit_price?.toString() || "",
      underlying_balance: scenario.underlying_balance?.toString() || "",
      underlying_rate: scenario.underlying_rate?.toString() || "",
      underlying_term_years: scenario.underlying_term_years?.toString() || "",
      underlying_monthly_piti: scenario.underlying_monthly_piti?.toString() || "",
      wrap_sale_price: scenario.wrap_sale_price?.toString() || "",
      wrap_down_payment: scenario.wrap_down_payment?.toString() || "",
      wrap_interest_rate: scenario.wrap_interest_rate?.toString() || "",
      wrap_amortization_years: scenario.wrap_amortization_years?.toString() || "30",
      wrap_balloon_years: scenario.wrap_balloon_years?.toString() || "",
      assignment_fee: scenario.assignment_fee?.toString() || ""
    });
    setShowScenarioModal(true);
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${BACKEND_URL}/api/notes`, {
        deal_id: dealId,
        content: noteForm.content,
        contact_outcome: noteForm.contact_outcome || null,
        note_type: noteForm.note_type
      }, { withCredentials: true });
      
      toast.success("Note added");
      setShowNoteModal(false);
      setNoteForm({ content: "", contact_outcome: "", note_type: "general" });
      fetchDealData();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${BACKEND_URL}/api/deals/${dealId}/photos`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo uploaded");
      fetchDealData();
    } catch (error) {
      toast.error("Failed to upload photo");
    }
  };

  const handleFileUpload = async (e, fileType = "other") => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${BACKEND_URL}/api/deals/${dealId}/files?file_type=${fileType}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("File uploaded");
      fetchDealData();
    } catch (error) {
      toast.error("Failed to upload file");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/photos/${photoId}`, { withCredentials: true });
      toast.success("Photo deleted");
      fetchDealData();
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/files/${fileId}`, { withCredentials: true });
      toast.success("File deleted");
      fetchDealData();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handleAddQuickLink = () => {
    if (!newLink.name || !newLink.url) return;
    const links = deal.quick_links || [];
    setDeal({ ...deal, quick_links: [...links, newLink] });
    setNewLink({ name: "", url: "" });
    setShowLinkModal(false);
  };

  const handleRemoveQuickLink = (index) => {
    const links = [...(deal.quick_links || [])];
    links.splice(index, 1);
    setDeal({ ...deal, quick_links: links });
  };

  // ============== COMPARABLES HANDLERS ==============
  
  const handleParsePropertyUrl = async () => {
    if (!urlToParse.trim()) {
      toast.error("Please enter a property URL");
      return;
    }
    
    setParsingUrl(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/deals/parse-property-url`,
        { url: urlToParse },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const prop = response.data.property;
        setCompForm({
          ...compForm,
          address: prop.address || "",
          sale_price: prop.price ? String(prop.price) : "",
          beds: prop.beds ? String(prop.beds) : "",
          baths: prop.baths ? String(prop.baths) : "",
          sqft: prop.sqft ? String(prop.sqft) : "",
          source_url: prop.source_url || urlToParse
        });
        toast.success(`Property data extracted from ${prop.source}`);
        setUrlToParse("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse property URL");
    } finally {
      setParsingUrl(false);
    }
  };
  
  const handleAddComparable = async (e) => {
    e.preventDefault();
    if (!compForm.address || !compForm.sale_price) {
      toast.error("Address and sale price are required");
      return;
    }
    
    setSavingComp(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/deals/${dealId}/comparables`,
        {
          address: compForm.address,
          sale_price: parseFloat(compForm.sale_price) || 0,
          sale_date: compForm.sale_date || null,
          beds: compForm.beds ? parseInt(compForm.beds) : null,
          baths: compForm.baths ? parseFloat(compForm.baths) : null,
          sqft: compForm.sqft ? parseInt(compForm.sqft) : null,
          distance_miles: compForm.distance_miles ? parseFloat(compForm.distance_miles) : null,
          condition: compForm.condition,
          notes: compForm.notes || null
        },
        { withCredentials: true }
      );
      
      // Update deal with new comparables
      setDeal({ 
        ...deal, 
        comparables: response.data.comparables
      });
      
      toast.success(`Comparable added. Suggested ARV: ${formatCurrency(response.data.suggested_arv)}`);
      setShowCompModal(false);
      setCompForm({
        address: "", sale_price: "", sale_date: "", beds: "", baths: "",
        sqft: "", distance_miles: "", condition: "similar", notes: "", source_url: ""
      });
      setUrlToParse("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add comparable");
    } finally {
      setSavingComp(false);
    }
  };

  const handleDeleteComparable = async (compId) => {
    if (!window.confirm("Delete this comparable?")) return;
    
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/deals/${dealId}/comparables/${compId}`,
        { withCredentials: true }
      );
      
      setDeal({ ...deal, comparables: response.data.comparables });
      toast.success("Comparable removed");
    } catch (error) {
      toast.error("Failed to remove comparable");
    }
  };

  // ============== MAO PERCENTAGE HANDLER ==============
  const handleUpdateMaoPercentage = async (newPercentage) => {
    if (newPercentage < 50 || newPercentage > 90) return;
    
    setSavingMaoPercent(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/deals/${dealId}/mao-percentage`,
        { mao_percentage: newPercentage },
        { withCredentials: true }
      );
      
      setDeal({ ...deal, mao_percentage: newPercentage });
      toast.success(`MAO rule updated to ${newPercentage}%`);
      
      // Refresh scenarios to reflect new MAO calculations
      const scenariosRes = await axios.get(
        `${BACKEND_URL}/api/deals/${dealId}/scenarios`,
        { withCredentials: true }
      );
      setScenarios(scenariosRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update MAO percentage");
    } finally {
      setSavingMaoPercent(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVerdictClass = (verdict) => {
    switch (verdict) {
      case "good": return "verdict-good";
      case "marginal": return "verdict-marginal";
      case "bad": return "verdict-bad";
      default: return "bg-white/[0.08] text-white/70";
    }
  };

  const getConfidenceClass = (confidence) => {
    switch (confidence) {
      case "strong": return "bg-emerald-500/20 text-emerald-500 border-emerald-500";
      case "marginal": return "bg-amber-500/20 text-amber-500 border-amber-500";
      case "high_risk": return "bg-red-500/20 text-red-500 border-red-500";
      default: return "bg-white/[0.08]/20 text-white/70 border-white/[0.1]";
    }
  };

  const getPriorityLabel = (score) => {
    if (score >= 70) return { label: "High Priority", class: "score-high" };
    if (score >= 40) return { label: "Medium Priority", class: "score-medium" };
    return { label: "Low Priority", class: "score-low" };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={`${mainClass} p-4 md:p-8 transition-all duration-300`}>
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  if (!deal) return null;

  const priority = getPriorityLabel(deal.priority_score);
  const confidence = deal.confidence_score || "marginal";
  const riskFlags = deal.risk_flags || [];

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`${mainClass} p-4 md:p-8 transition-all duration-300`} data-testid="deal-detail-page">
        {/* Header - Full Width */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
          <div className="flex-1 min-w-0">
            <Link to="/deals" className="inline-flex items-center gap-2 text-white/70 hover:text-gold mb-3">
              <ArrowLeft className="w-4 h-4" />
              Back to Deals
            </Link>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-white">{deal.property_address}</h1>
              {deal.is_locked && (
                <span className="px-2 py-1 bg-amber-500/20 border border-amber-500/40 text-amber-500 text-xs font-bold uppercase">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Locked
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <span className="text-sm text-white/70">{deal.property_type}</span>
              {deal.beds && <span className="text-sm text-white/70">• {deal.beds} bed</span>}
              {deal.baths && <span className="text-sm text-white/70">• {deal.baths} bath</span>}
              {deal.sqft && <span className="text-sm text-white/70">• {deal.sqft.toLocaleString()} sqft</span>}
              {deal.lead_source && (
                <span className="text-xs px-2 py-0.5 bg-white/[0.08] text-white/70">
                  Source: {deal.lead_source}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            {/* Confidence Meter */}
            <div className="text-right hidden md:block">
              <div className={`inline-block px-3 py-1.5 border ${getConfidenceClass(confidence)}`}>
                {confidence === "strong" && <CheckCircle className="w-4 h-4 inline mr-1" />}
                {confidence === "high_risk" && <AlertTriangle className="w-4 h-4 inline mr-1" />}
                {confidence.replace('_', ' ').toUpperCase()}
              </div>
              <p className="text-xs text-white/70 mt-1">Confidence</p>
            </div>
            
            <div className="text-right hidden md:block">
              <div className={`inline-block px-3 py-1.5 font-mono text-lg font-bold ${priority.class}`}>
                {deal.priority_score}
              </div>
              <p className="text-xs text-white/70 mt-1">{priority.label}</p>
            </div>
        
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleToggleLock}
                className="border-white/[0.1] text-white/70 hover:border-gold hover:text-gold rounded-none h-9 px-3"
              >
                {deal.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              </Button>
              
              <Button 
                onClick={handleSaveDeal} 
                disabled={saving || deal.is_locked}
                className="gold-gradient-btn rounded-none font-bold h-9"
                data-testid="save-deal-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>

        {/* Execution Cockpit Header - Transaction Intelligence Strip */}
        <ExecutionCockpitHeader 
          deal={deal} 
          guidance={guidance} 
          aiScore={deal.ai_score}
        />

        {/* Workflow Actions - Stage-based next steps */}
        <WorkflowActions 
          deal={deal} 
          onDealUpdate={(updatedDeal) => setDeal(updatedDeal)}
        />

        {/* Deal Command Center - AI-Powered Execution Cockpit */}
        {guidance && (
          <DealCommandCenter 
            deal={deal}
            guidance={guidance}
            scenarios={scenarios}
            onAction={async (action) => {
              // Handle advance stage action
              if (action.action_type === "advance_stage") {
                await advanceDealStage();
              } else if (action.route?.includes('?tab=')) {
                const tab = action.route.split('?tab=')[1];
                setActiveTab(tab);
                // Auto-advance when switching tabs (completing an action)
                if (autoAdvanceEnabled && !['closed', 'dead', 'sold'].includes(deal.pipeline_stage)) {
                  await advanceDealStage();
                }
              } else if (action.route && action.route !== `/deals/${deal.deal_id}`) {
                // Auto-advance before navigating (if enabled)
                if (autoAdvanceEnabled && !['closed', 'dead', 'sold'].includes(deal.pipeline_stage)) {
                  await advanceDealStage();
                }
                navigate(action.route);
              }
            }}
            className="mb-6"
          />
        )}

        {/* Risk Flags Banner */}
        {riskFlags.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-red-500 mb-1">Risk Flags</h4>
                <div className="flex flex-wrap gap-2">
                  {riskFlags.map((flag, i) => (
                    <span key={`risk-flag-${flag}-${i}`} className="text-xs px-2 py-1 bg-red-500/20 text-[#FF3B30]">
                      {flag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Two Column Layout: Main Content + Tools Sidebar */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Stage Control Bar - Compact */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-3 mb-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-white/50 uppercase">Stage</Label>
                  <Select 
                    value={deal.pipeline_stage} 
                    onValueChange={async (v) => {
                      try {
                        await axios.put(
                          `${BACKEND_URL}/api/deals/${deal.deal_id}`,
                          { pipeline_stage: v },
                          { withCredentials: true }
                        );
                        setDeal({...deal, pipeline_stage: v});
                        toast.success(`Stage updated to ${v.replace(/_/g, ' ')}`);
                        const guidanceRes = await axios.get(
                          `${BACKEND_URL}/api/deals/${deal.deal_id}/guidance`,
                          { withCredentials: true }
                        );
                        setGuidance(guidanceRes.data);
                      } catch (error) {
                        toast.error("Failed to update stage");
                      }
                    }}
                    disabled={deal.is_locked}
                  >
                    <SelectTrigger className="w-40 bg-black border-white/[0.08] text-white h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                      {stages.map(stage => (
                        <SelectItem key={stage.value} value={stage.value} className="text-white hover:bg-white/[0.06]">
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Auto-Advance Toggle */}
                <div className="flex items-center gap-2 pl-4 border-l border-white/[0.08]">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setAutoAdvanceEnabled(!autoAdvanceEnabled)}
                        className={`relative w-9 h-5 rounded-full transition-all duration-300 ${
                          autoAdvanceEnabled 
                            ? 'bg-gold/30 border border-gold/50' 
                            : 'bg-white/[0.08] border border-white/[0.08]'
                        }`}
                        data-testid="auto-advance-toggle"
                      >
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-all duration-300 ${
                          autoAdvanceEnabled 
                            ? 'left-4 bg-gold shadow-[0_0_8px_rgba(212,175,55,0.5)]' 
                            : 'left-0.5 bg-white/30'
                        }`} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-black border-white/[0.08] text-white">
                      <p className="text-xs">Auto-advance stage on action</p>
                    </TooltipContent>
                  </Tooltip>
                  <Label className="text-[10px] text-white/50">AUTO</Label>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="goldOutline"
                  size="sm"
                  onClick={() => advanceDealStage()}
                  disabled={deal.is_locked || ['closed', 'dead', 'sold'].includes(deal.pipeline_stage)}
                  className="gap-1.5 h-8"
                  data-testid="advance-stage-btn"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                  Advance
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={packageShareUrl ? () => { navigator.clipboard.writeText(packageShareUrl); toast.success("Link copied!"); } : generateDealPackage}
                  disabled={generatingPackage}
                  className={`gap-1.5 h-8 rounded-none ${packageShareUrl ? "border-[#00FF88]/30 text-[#00FF88] hover:bg-[#00FF88]/[0.06]" : "border-white/[0.1] text-white/50 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"}`}
                  data-testid="share-package-btn"
                >
                  {generatingPackage ? <><Brain className="w-3.5 h-3.5 animate-pulse" /> Generating…</> : packageShareUrl ? <><CheckCircle className="w-3.5 h-3.5" /> Copy Link</> : <><Zap className="w-3.5 h-3.5" /> Share Package</>}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCloseModal(true)}
                  className="border-white/[0.1] text-white/50 hover:border-red-500 hover:text-red-500 rounded-none h-8"
                >
                  Close/Kill
                </Button>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-black pb-2">
                <TabsList className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-1 rounded-none inline-flex min-w-max">
                  <TabsTrigger value="property" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap">
                    <Home className="w-4 h-4 mr-2" />Property
                  </TabsTrigger>
                  <TabsTrigger value="owner" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap">
                    <User className="w-4 h-4 mr-2" />Owner
                  </TabsTrigger>
                  <TabsTrigger value="calculator" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap">
                    <Calculator className="w-4 h-4 mr-2" />Calculator
                  </TabsTrigger>
                  <TabsTrigger value="comps" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap" data-testid="comps-tab">
                    <BarChart3 className="w-4 h-4 mr-2" />Comps
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap">
                    <MessageSquare className="w-4 h-4 mr-2" />Activity
                  </TabsTrigger>
                  <TabsTrigger value="photos" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap">
                    <Image className="w-4 h-4 mr-2" />Photos
                  </TabsTrigger>
                  <TabsTrigger value="files" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap">
                    <FolderOpen className="w-4 h-4 mr-2" />Files
                  </TabsTrigger>
                  <TabsTrigger value="lenders" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap">
                    <Building2 className="w-4 h-4 mr-2" />Lenders
                  </TabsTrigger>
                  <TabsTrigger value="ai-memo" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap" data-testid="ai-memo-tab">
                    <Brain className="w-4 h-4 mr-2" />AI Memo
                  </TabsTrigger>
                  <TabsTrigger value="property-intel" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap" data-testid="property-intel-tab">
                    <MapPin className="w-4 h-4 mr-2" />Property Intel
                  </TabsTrigger>
                  <TabsTrigger value="timeline" className="data-[state=active]:bg-gold data-[state=active]:text-black rounded-none whitespace-nowrap" data-testid="timeline-tab">
                    <Calendar className="w-4 h-4 mr-2" />Timeline
                  </TabsTrigger>
                </TabsList>
              </div>

          {/* Property Tab */}
          <TabsContent value="property" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Property Information</h2>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white/70">Property Address</Label>
                    <Input
                      value={deal.property_address}
                      onChange={(e) => setDeal({...deal, property_address: e.target.value})}
                      className="bg-black border-white/[0.08] text-white"
                      disabled={deal.is_locked}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Asking Price ($)</Label>
                      <Input
                        type="number"
                        value={deal.asking_price || ""}
                        onChange={(e) => setDeal({...deal, asking_price: parseFloat(e.target.value) || null})}
                        className="bg-black border-white/[0.08] text-white"
                        disabled={deal.is_locked}
                        placeholder="250000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Lead Source</Label>
                      <Input
                        value={deal.lead_source || ""}
                        onChange={(e) => setDeal({...deal, lead_source: e.target.value})}
                        className="bg-black border-white/[0.08] text-white"
                        disabled={deal.is_locked}
                        placeholder="Direct Mail, etc."
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Parcel ID / APN</Label>
                      <Input
                        value={deal.parcel_id || ""}
                        onChange={(e) => setDeal({...deal, parcel_id: e.target.value})}
                        className="bg-black border-white/[0.08] text-white"
                        disabled={deal.is_locked}
                        placeholder="123-456-789"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">County</Label>
                      <Input
                        value={deal.county || ""}
                        onChange={(e) => setDeal({...deal, county: e.target.value})}
                        className="bg-black border-white/[0.08] text-white"
                        disabled={deal.is_locked}
                        placeholder="Travis County"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white/70">Legal Description</Label>
                    <Textarea
                      value={deal.legal_description || ""}
                      onChange={(e) => setDeal({...deal, legal_description: e.target.value})}
                      className="bg-black border-white/[0.08] text-white min-h-[60px]"
                      disabled={deal.is_locked}
                      placeholder="Lot 5, Block 3, Oak Hills Subdivision"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Property Type</Label>
                      <Select 
                        value={deal.property_type} 
                        onValueChange={(v) => setDeal({...deal, property_type: v})}
                        disabled={deal.is_locked}
                      >
                        <SelectTrigger className="bg-black border-white/[0.08] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                          {["SFH", "MF", "Condo", "Land", "Commercial"].map(type => (
                            <SelectItem key={type} value={type} className="text-white hover:bg-white/[0.06]">{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Urgency (1-10)</Label>
                      <Input
                        type="number" min="1" max="10"
                        value={deal.urgency || 5}
                        onChange={(e) => setDeal({...deal, urgency: Math.min(10, Math.max(1, parseInt(e.target.value) || 5))})}
                        className="bg-black border-white/[0.08] text-white"
                        disabled={deal.is_locked}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Beds</Label>
                      <Input type="number" value={deal.beds || ""} onChange={(e) => setDeal({...deal, beds: parseInt(e.target.value) || null})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Baths</Label>
                      <Input type="number" step="0.5" value={deal.baths || ""} onChange={(e) => setDeal({...deal, baths: parseFloat(e.target.value) || null})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Sqft</Label>
                      <Input type="number" value={deal.sqft || ""} onChange={(e) => setDeal({...deal, sqft: parseInt(e.target.value) || null})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white/70">Property Notes</Label>
                    <Textarea value={deal.property_notes || ""} onChange={(e) => setDeal({...deal, property_notes: e.target.value})} className="bg-black border-white/[0.08] text-white min-h-[100px]" disabled={deal.is_locked} />
                  </div>
                </div>
              </div>

              {/* Assumptions Panel */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">
                  Analysis Assumptions
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 inline ml-2 text-white/70" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-black border-white/[0.08] text-white">
                      <p>These assumptions affect scenario calculations</p>
                    </TooltipContent>
                  </Tooltip>
                </h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Monthly Rent</Label>
                      <Input type="number" value={deal.monthly_rent || ""} onChange={(e) => setDeal({...deal, monthly_rent: parseFloat(e.target.value) || null})} className="bg-black border-white/[0.08] text-white" placeholder="$0" disabled={deal.is_locked} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Exit Price (ARV)</Label>
                      <Input type="number" value={deal.arv || deal.exit_price || ""} onChange={(e) => {
                        const value = parseFloat(e.target.value) || null;
                        setDeal({...deal, arv: value, exit_price: value});
                      }} className="bg-black border-white/[0.08] text-white" placeholder="$0" disabled={deal.is_locked} />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Hold (months)</Label>
                      <Input type="number" value={deal.hold_months || 12} onChange={(e) => setDeal({...deal, hold_months: parseInt(e.target.value) || 12})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Vacancy %</Label>
                      <Input type="number" value={deal.vacancy_percent || 8} onChange={(e) => setDeal({...deal, vacancy_percent: parseFloat(e.target.value) || 8})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Expense %</Label>
                      <Input type="number" value={deal.expense_percent || 35} onChange={(e) => setDeal({...deal, expense_percent: parseFloat(e.target.value) || 35})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Owner Tab */}
          <TabsContent value="owner" className="space-y-6">
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
              <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Owner Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white/70"><User className="w-4 h-4 inline mr-2" />Owner Name</Label>
                  <Input value={deal.owner_name || ""} onChange={(e) => setDeal({...deal, owner_name: e.target.value})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70"><Phone className="w-4 h-4 inline mr-2" />Phone</Label>
                  <Input type="tel" value={deal.owner_phone || ""} onChange={(e) => setDeal({...deal, owner_phone: e.target.value})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70"><Mail className="w-4 h-4 inline mr-2" />Email</Label>
                  <Input type="email" value={deal.owner_email || ""} onChange={(e) => setDeal({...deal, owner_email: e.target.value})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70"><MapPin className="w-4 h-4 inline mr-2" />Mailing Address</Label>
                  <Input value={deal.owner_mailing_address || ""} onChange={(e) => setDeal({...deal, owner_mailing_address: e.target.value})} className="bg-black border-white/[0.08] text-white" disabled={deal.is_locked} />
                </div>
              </div>
              
              <div className="mt-6 space-y-2">
                <Label className="text-white/70">Owner Notes</Label>
                <Textarea value={deal.owner_notes || ""} onChange={(e) => setDeal({...deal, owner_notes: e.target.value})} className="bg-black border-white/[0.08] text-white min-h-[100px]" disabled={deal.is_locked} />
              </div>
            </div>
            
            {/* Offer Notes */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
              <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Offer Notes</h2>
              <Textarea
                value={deal.offer_notes || ""}
                onChange={(e) => setDeal({...deal, offer_notes: e.target.value})}
                className="bg-black border-white/[0.08] text-white min-h-[150px]"
                placeholder="Document offer details, negotiation points, call summaries..."
                disabled={deal.is_locked}
              />
            </div>
          </TabsContent>

          {/* Calculator Tab */}
          <TabsContent value="calculator" className="space-y-6">
            {/* Rehab Calculator */}
            <RehabCalculator 
              deal={deal} 
              onEstimateUpdate={async (estimate) => {
                // Update local state
                setDeal({ ...deal, rehab_estimate: estimate });
                
                // Save to backend
                try {
                  await axios.put(
                    `${BACKEND_URL}/api/deals/${deal.deal_id}`,
                    { rehab_estimate: estimate },
                    { withCredentials: true }
                  );
                  
                  // Recalculate all scenarios with new rehab estimate
                  await axios.post(
                    `${BACKEND_URL}/api/deals/${deal.deal_id}/recalculate-scenarios`,
                    {},
                    { withCredentials: true }
                  );
                  
                  toast.success(`Rehab estimate updated: $${estimate.toLocaleString()}`);
                  
                  // Refresh scenarios to show updated MAO
                  const scenariosRes = await axios.get(
                    `${BACKEND_URL}/api/deals/${deal.deal_id}/scenarios`,
                    { withCredentials: true }
                  );
                  setScenarios(scenariosRes.data);
                  
                  // Refresh guidance
                  const guidanceRes = await axios.get(
                    `${BACKEND_URL}/api/deals/${deal.deal_id}/guidance`,
                    { withCredentials: true }
                  );
                  setGuidance(guidanceRes.data);
                } catch (error) {
                  console.error("Failed to save rehab estimate:", error);
                  toast.error("Failed to update rehab estimate");
                }
              }} 
            />

            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-white uppercase">Strategy Scenarios</h2>
              
              {tier === "starter" && scenarios.length >= 1 ? (
                <div className="flex items-center gap-2 text-amber-500 bg-amber-500/10 px-3 py-1.5 border border-amber-500/30">
                  <Crown className="w-4 h-4" />
                  <span className="text-sm">Upgrade to Pro for multi-scenario analysis</span>
                </div>
              ) : (
                <Button 
                  onClick={() => { resetScenarioForm(); setShowScenarioModal(true); }}
                  className="bg-gold text-black hover:bg-gold-light rounded-none font-bold"
                  data-testid="add-scenario-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Scenario
                </Button>
              )}
            </div>

            {scenarios.length === 0 ? (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
                <Calculator className="w-16 h-16 text-white/70 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-bold text-white mb-2">No Scenarios Yet</h3>
                <p className="text-white/70 mb-6">Add a scenario to analyze this deal's potential</p>
                <Button onClick={() => { resetScenarioForm(); setShowScenarioModal(true); }} className="gold-gradient-btn rounded-none font-bold">
                  <Plus className="w-4 h-4 mr-2" />Create First Scenario
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {scenarios.map((scenario) => (
                  <div key={scenario.scenario_id} className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] overflow-hidden">
                    <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
                      <div>
                        <h3 className="font-heading font-bold text-white">{scenario.name}</h3>
                        <span className="text-xs text-white/70 uppercase">{scenario.strategy.replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 text-xs font-bold uppercase ${getVerdictClass(scenario.verdict)}`}>
                          {scenario.verdict}
                        </span>
                        <button onClick={() => handleEditScenario(scenario)} className="p-1.5 text-white/70 hover:text-gold transition-all duration-300">
                          <FileText className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteScenario(scenario.scenario_id)} className="p-1.5 text-white/70 hover:text-[#FF3B30] transition-all duration-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-4 space-y-4">
                      {/* Strategy-specific outputs */}
                      {scenario.strategy === "subject_to" && (
                        <div className="grid grid-cols-2 gap-4">
                          <div><p className="text-xs text-white/70 uppercase">Loan Balance</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.existing_loan_balance)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Monthly Payment</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.monthly_payment)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Monthly Cash Flow</p><p className={`font-mono text-lg ${scenario.monthly_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(scenario.monthly_cash_flow)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Cash Required</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.cash_required)}</p></div>
                        </div>
                      )}
                      
                      {scenario.strategy === "seller_finance" && (
                        <div className="grid grid-cols-2 gap-4">
                          <div><p className="text-xs text-white/70 uppercase">Purchase Price</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.purchase_price)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Monthly Payment</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.monthly_payment)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Down Payment</p><p className="font-mono text-lg text-gold">{formatCurrency(scenario.down_payment)}</p></div>
                          {scenario.balloon_amount > 0 && (
                            <div><p className="text-xs text-white/70 uppercase">Balloon Due</p><p className="font-mono text-lg text-amber-500">{formatCurrency(scenario.balloon_amount)}</p></div>
                          )}
                        </div>
                      )}
                      
                      {scenario.strategy === "wrap" && (
                        <div className="grid grid-cols-2 gap-4">
                          <div><p className="text-xs text-white/70 uppercase">Buyer Payment</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.buyer_payment)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Underlying Payment</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.underlying_payment)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Monthly Spread</p><p className={`font-mono text-2xl font-bold ${scenario.monthly_spread >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(scenario.monthly_spread)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Buyer Down</p><p className="font-mono text-lg text-gold">{formatCurrency(scenario.wrap_down_payment)}</p></div>
                        </div>
                      )}
                      
                      {!["subject_to", "seller_finance", "wrap"].includes(scenario.strategy) && (
                        <div className="grid grid-cols-2 gap-4">
                          <div><p className="text-xs text-white/70 uppercase">ARV</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.arv)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Purchase</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.purchase_price)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">MAO</p><p className="font-mono text-lg text-gold">{formatCurrency(scenario.mao)}</p></div>
                          <div><p className="text-xs text-white/70 uppercase">Cash Required</p><p className="font-mono text-lg text-white">{formatCurrency(scenario.cash_required)}</p></div>
                        </div>
                      )}
                      
                      <div className="pt-4 border-t border-white/[0.08] grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-white/70 uppercase">Est. Profit</p>
                          <p className={`font-mono text-2xl font-bold ${scenario.estimated_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {formatCurrency(scenario.estimated_profit)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-white/70 uppercase">ROI</p>
                          <p className={`font-mono text-2xl font-bold ${scenario.roi >= 15 ? 'text-emerald-500' : 'text-amber-500'}`}>
                            {scenario.roi?.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                      
                      {/* Risk Flags */}
                      {scenario.risk_flags?.length > 0 && (
                        <div className="pt-3 border-t border-white/[0.08]">
                          <div className="flex flex-wrap gap-1">
                            {scenario.risk_flags.map((flag, i) => (
                              <span key={`scenario-flag-${flag}-${i}`} className="text-xs px-2 py-0.5 bg-red-500/20 text-[#FF3B30] border border-red-500/30">
                                {flag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Comps Tab - Comparable Properties */}
          <TabsContent value="comps" className="space-y-6" data-testid="comps-tab-content">
            {/* MAO Percentage Adjustment */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading text-lg font-bold text-white uppercase flex items-center gap-2">
                    <Percent className="w-5 h-5 text-gold" />
                    MAO Rule Adjustment
                  </h2>
                  <p className="text-white/70 text-sm mt-1">
                    Adjust the percentage used in Maximum Allowable Offer calculations
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-3xl font-mono font-bold text-gold">{deal.mao_percentage || 70}%</span>
                  <p className="text-xs text-white/70">of ARV</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-white/70 text-sm w-12">50%</span>
                  <Slider
                    value={[deal.mao_percentage || 70]}
                    onValueChange={(values) => setDeal({ ...deal, mao_percentage: values[0] })}
                    onValueCommit={(values) => handleUpdateMaoPercentage(values[0])}
                    min={50}
                    max={90}
                    step={1}
                    className="flex-1"
                    disabled={deal.is_locked || savingMaoPercent}
                    data-testid="mao-slider"
                  />
                  <span className="text-white/70 text-sm w-12 text-right">90%</span>
                </div>
                
                <div className="grid grid-cols-3 gap-4 pt-2 border-t border-white/[0.08]">
                  <button 
                    onClick={() => handleUpdateMaoPercentage(65)}
                    className={`p-2 text-center border transition-all duration-300 ${
                      deal.mao_percentage === 65 
                        ? "border-gold bg-gold/10 text-gold" 
                        : "border-white/[0.08] text-white/70 hover:border-white/[0.15]"
                    }`}
                    disabled={deal.is_locked}
                  >
                    <span className="text-lg font-mono font-bold">65%</span>
                    <p className="text-[10px] uppercase">Conservative</p>
                  </button>
                  <button 
                    onClick={() => handleUpdateMaoPercentage(70)}
                    className={`p-2 text-center border transition-all duration-300 ${
                      (deal.mao_percentage || 70) === 70 
                        ? "border-gold bg-gold/10 text-gold" 
                        : "border-white/[0.08] text-white/70 hover:border-white/[0.15]"
                    }`}
                    disabled={deal.is_locked}
                  >
                    <span className="text-lg font-mono font-bold">70%</span>
                    <p className="text-[10px] uppercase">Standard</p>
                  </button>
                  <button 
                    onClick={() => handleUpdateMaoPercentage(75)}
                    className={`p-2 text-center border transition-all duration-300 ${
                      deal.mao_percentage === 75 
                        ? "border-gold bg-gold/10 text-gold" 
                        : "border-white/[0.08] text-white/70 hover:border-white/[0.15]"
                    }`}
                    disabled={deal.is_locked}
                  >
                    <span className="text-lg font-mono font-bold">75%</span>
                    <p className="text-[10px] uppercase">Aggressive</p>
                  </button>
                </div>
                
                {savingMaoPercent && (
                  <p className="text-xs text-gold animate-pulse">Updating calculations...</p>
                )}
              </div>
            </div>

            {/* ATTOM Live Comparable Sales */}
            <CompsPanel deal={deal} onArvUpdate={fetchDealData} />

            {/* Manual Comparable Properties */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading text-lg font-bold text-white uppercase flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-gold" />
                    My Comparable Properties
                  </h2>
                  <p className="text-white/70 text-sm mt-1">
                    Add recent sales to justify your After-Repair Value (ARV)
                  </p>
                </div>
                <Button 
                  onClick={() => setShowCompModal(true)}
                  className="bg-gold text-black hover:bg-gold-light rounded-none font-bold"
                  disabled={deal.is_locked}
                  data-testid="add-comp-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Comp
                </Button>
              </div>

              {/* Suggested ARV */}
              {deal.comparables?.length > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-medium">Suggested ARV from Comps</p>
                      <p className="text-xs text-white/70 mt-1">Based on {deal.comparables.length} comparable{deal.comparables.length > 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-mono font-bold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
                        {formatCurrency(
                          deal.comparables.reduce((sum, c) => sum + (c.sale_price || 0), 0) / deal.comparables.length
                        )}
                      </p>
                      <button 
                        onClick={async () => {
                          const avgPrice = deal.comparables.reduce((sum, c) => sum + (c.sale_price || 0), 0) / deal.comparables.length;
                          setDeal({ ...deal, arv: avgPrice });
                          await handleSaveDeal();
                          toast.success("ARV updated from comps");
                        }}
                        className="text-xs text-gold hover:text-gold-light mt-1"
                        disabled={deal.is_locked}
                      >
                        Apply as ARV →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Comps Grid */}
              {(!deal.comparables || deal.comparables.length === 0) ? (
                <div className="p-12 text-center border border-dashed border-white/[0.08]">
                  <BarChart3 className="w-16 h-16 text-white/70 mx-auto mb-4" />
                  <h3 className="text-xl font-heading font-bold text-white mb-2">No Comparables Yet</h3>
                  <p className="text-white/70 mb-4">Add comparable sales to support your ARV valuation</p>
                  <Button 
                    onClick={() => setShowCompModal(true)}
                    className="gold-gradient-btn rounded-none font-bold"
                    disabled={deal.is_locked}
                  >
                    <Plus className="w-4 h-4 mr-2" />Add First Comparable
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {deal.comparables.map((comp) => (
                    <div key={comp.comp_id} className="bg-black border border-white/[0.08] p-4 group">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium truncate">{comp.address}</h4>
                          <p className="text-xs text-white/70 mt-1">
                            {comp.beds && `${comp.beds} bed`}
                            {comp.baths && ` • ${comp.baths} bath`}
                            {comp.sqft && ` • ${comp.sqft.toLocaleString()} sqft`}
                          </p>
                        </div>
                        <button 
                          onClick={() => handleDeleteComparable(comp.comp_id)}
                          className="text-white/70 hover:text-[#FF3B30] opacity-0 group-hover:opacity-100 transition-opacity p-1"
                          disabled={deal.is_locked}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-white/70 uppercase">Sale Price</p>
                          <p className="font-mono text-lg text-gold font-bold">{formatCurrency(comp.sale_price)}</p>
                        </div>
                        {comp.sale_date && (
                          <div>
                            <p className="text-xs text-white/70 uppercase">Sale Date</p>
                            <p className="text-sm text-white">{new Date(comp.sale_date).toLocaleDateString()}</p>
                          </div>
                        )}
                        {comp.distance_miles && (
                          <div>
                            <p className="text-xs text-white/70 uppercase">Distance</p>
                            <p className="text-sm text-white">{comp.distance_miles} mi</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-white/70 uppercase">Condition</p>
                          <span className={`text-xs px-2 py-0.5 uppercase ${
                            comp.condition === 'superior' ? 'bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' :
                            comp.condition === 'inferior' ? 'bg-red-500/20 text-[#FF3B30]' :
                            'bg-white/[0.08] text-white/70'
                          }`}>
                            {comp.condition}
                          </span>
                        </div>
                      </div>
                      
                      {comp.notes && (
                        <p className="text-xs text-white/70 mt-3 pt-3 border-t border-white/[0.06]">{comp.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Notes/Activity Tab */}
          <TabsContent value="notes" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-white uppercase">Activity & Notes</h2>
              <Button onClick={() => setShowNoteModal(true)} className="bg-gold text-black hover:bg-gold-light rounded-none font-bold" data-testid="add-note-btn">
                <Plus className="w-4 h-4 mr-2" />Add Note
              </Button>
            </div>

            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
              {activities.length === 0 ? (
                <div className="p-12 text-center">
                  <MessageSquare className="w-16 h-16 text-white/70 mx-auto mb-4" />
                  <h3 className="text-xl font-heading font-bold text-white mb-2">No Activity Yet</h3>
                  <p className="text-white/70">Activity and notes will appear here</p>
                </div>
              ) : (
                <div className="divide-y divide-white/[0.06]">
                  {activities.map((activity) => (
                    <div key={activity.note_id} className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-white">{activity.content}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {activity.contact_outcome && (
                              <span className="px-2 py-1 bg-white/[0.08] text-white/70 text-xs uppercase">{activity.contact_outcome}</span>
                            )}
                            {activity.note_type && activity.note_type !== "general" && (
                              <span className="px-2 py-1 bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] text-xs uppercase">{activity.note_type}</span>
                            )}
                            {activity.auto_type && (
                              <span className="px-2 py-1 bg-gold/10 text-gold text-xs uppercase">{activity.auto_type.replace('_', ' ')}</span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-white/70 whitespace-nowrap">{formatDate(activity.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-white uppercase">Photos</h2>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                <Button asChild className="bg-gold text-black hover:bg-gold-light rounded-none font-bold">
                  <span><Upload className="w-4 h-4 mr-2" />Upload Photo</span>
                </Button>
              </label>
            </div>

            {photos.length === 0 ? (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
                <Image className="w-16 h-16 text-white/70 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-bold text-white mb-2">No Photos Yet</h3>
                <p className="text-white/70 mb-6">Upload photos of the property</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((photo) => (
                  <div key={photo.photo_id} className="relative group aspect-square bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] overflow-hidden">
                    <img src={`${BACKEND_URL}/api/photos/${photo.photo_id}`} alt={photo.filename} className="w-full h-full object-cover" />
                    <button onClick={() => handleDeletePhoto(photo.photo_id)} className="absolute top-2 right-2 p-2 bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-white uppercase">Deal Files</h2>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf" onChange={(e) => handleFileUpload(e, "pdf")} className="hidden" />
                  <Button asChild variant="outline" className="border-white/[0.1] text-white/70 rounded-none">
                    <span><FileText className="w-4 h-4 mr-2" />PDF</span>
                  </Button>
                </label>
                <label className="cursor-pointer">
                  <input type="file" onChange={(e) => handleFileUpload(e, "contract")} className="hidden" />
                  <Button asChild variant="outline" className="border-white/[0.1] text-white/70 rounded-none">
                    <span><FileText className="w-4 h-4 mr-2" />Contract</span>
                  </Button>
                </label>
                <label className="cursor-pointer">
                  <input type="file" onChange={(e) => handleFileUpload(e, "other")} className="hidden" />
                  <Button asChild className="bg-gold text-black hover:bg-gold-light rounded-none font-bold">
                    <span><Upload className="w-4 h-4 mr-2" />Upload File</span>
                  </Button>
                </label>
              </div>
            </div>

            {files.length === 0 ? (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
                <FolderOpen className="w-16 h-16 text-white/70 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-bold text-white mb-2">No Files Yet</h3>
                <p className="text-white/70">Upload contracts, inspections, PDFs, and other documents</p>
              </div>
            ) : (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] divide-y divide-white/[0.06]">
                {files.map((file) => (
                  <div key={file.file_id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-white/70" />
                      <div>
                        <p className="text-white font-medium">{file.filename}</p>
                        <p className="text-xs text-white/70">{file.file_type} • {Math.round(file.size / 1024)} KB</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`${BACKEND_URL}/api/files/${file.file_id}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="text-white/70 hover:text-gold">View</Button>
                      </a>
                      <button onClick={() => handleDeleteFile(file.file_id)} className="p-1.5 text-white/70 hover:text-[#FF3B30]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Lenders Tab */}
          <TabsContent value="lenders" className="space-y-6">
            <LendersTab deal={deal} />
          </TabsContent>

          <TabsContent value="ai-memo" className="space-y-6">
            <DealMemoGenerator deal={deal} />
          </TabsContent>

          <TabsContent value="property-intel" className="space-y-6">
            <PropertyIntelligence deal={deal} />
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="space-y-6">
            <DealTimelineTab dealId={deal.deal_id} />
          </TabsContent>
        </Tabs>
          </div>

          {/* Right Sidebar - Tools (stacks below on mobile) */}
          <div className="w-full lg:w-80 flex-shrink-0 space-y-4">
            {/* Smart Templates Panel */}
            <SmartTemplatesPanel deal={deal} />

            {/* Deal Network Panel */}
            <DealNetworkPanel deal={deal} />

            {/* Deal Wallet Panel */}
            <EscrowPanel dealId={deal.deal_id} />
            
            {/* Marketplace Control Panel */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-sm font-bold text-white uppercase flex items-center gap-2">
                  <Store className="w-4 h-4 text-gold" />
                  Marketplace
                </h3>
                {/* Status Badge */}
                <span className={`px-2 py-1 text-[9px] font-bold uppercase tracking-wider rounded ${
                  deal.marketplace_status === 'live' ? 'bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20' :
                  deal.marketplace_status === 'draft' ? 'bg-[#FF9F0A]/10 text-[#FF9F0A] border border-[#FF9F0A]/20' :
                  deal.marketplace_status === 'paused' ? 'bg-[#BF5AF2]/10 text-[#BF5AF2] border border-[#BF5AF2]/20' :
                  deal.marketplace_status === 'funded' ? 'bg-[#0A84FF]/10 text-[#0A84FF] border border-[#0A84FF]/20' :
                  'bg-white/[0.05] text-white/50 border border-white/[0.08]'
                }`}>
                  {deal.marketplace_status || 'Hidden'}
                </span>
              </div>
              
              <p className="text-white/50 text-xs mb-4">
                {deal.marketplace_status === 'live' 
                  ? 'This deal is visible in the marketplace and open for bids.'
                  : deal.marketplace_status === 'draft'
                  ? 'Draft saved. Complete required fields to publish.'
                  : deal.marketplace_status === 'paused'
                  ? 'Listing temporarily paused from marketplace.'
                  : 'This deal is private. Publish to the marketplace to attract capital and partners.'}
              </p>
              
              {/* Action Buttons */}
              <div className="space-y-2">
                {deal.marketplace_status !== 'live' ? (
                  <Button
                    onClick={async () => {
                      try {
                        await axios.put(
                          `${BACKEND_URL}/api/deals/${deal.deal_id}/marketplace-status`,
                          { status: 'live' },
                          { withCredentials: true }
                        );
                        setDeal({...deal, marketplace_status: 'live'});
                        toast.success('Deal published to marketplace!');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to publish deal');
                      }
                    }}
                    className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-semibold gap-2"
                    data-testid="publish-marketplace-btn"
                  >
                    <Store className="w-4 h-4" />
                    Post to Marketplace
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={async () => {
                        try {
                          await axios.put(
                            `${BACKEND_URL}/api/deals/${deal.deal_id}/marketplace-status`,
                            { status: 'paused' },
                            { withCredentials: true }
                          );
                          setDeal({...deal, marketplace_status: 'paused'});
                          toast.success('Listing paused');
                        } catch (error) {
                          toast.error('Failed to pause listing');
                        }
                      }}
                      variant="outline"
                      className="w-full border-white/[0.1] text-white/70 hover:border-[#BF5AF2] hover:text-[#BF5AF2]"
                    >
                      <Pause className="w-4 h-4 mr-2" />
                      Pause Listing
                    </Button>
                    <Link to="/marketplace" className="block">
                      <Button variant="ghost" className="w-full text-gold hover:text-gold-light text-xs">
                        View Marketplace Listing
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </Button>
                    </Link>
                  </>
                )}
                
                {deal.marketplace_status === 'paused' && (
                  <Button
                    onClick={async () => {
                      try {
                        await axios.put(
                          `${BACKEND_URL}/api/deals/${deal.deal_id}/marketplace-status`,
                          { status: 'hidden' },
                          { withCredentials: true }
                        );
                        setDeal({...deal, marketplace_status: 'hidden'});
                        toast.success('Deal removed from marketplace');
                      } catch (error) {
                        toast.error('Failed to unpublish deal');
                      }
                    }}
                    variant="ghost"
                    className="w-full text-white/50 hover:text-[#FF3B30] text-xs"
                  >
                    Unpublish Completely
                  </Button>
                )}
              </div>
            </div>

            {/* Capital Request Panel */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-sm font-bold text-white uppercase flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-gold" />
                  Capital Request
                </h3>
                {fundingRequests.length > 0 && (
                  <span className="px-2 py-0.5 bg-gold/10 text-gold text-[9px] font-bold rounded">
                    {fundingRequests.filter(r => r.status === 'open').length} ACTIVE
                  </span>
                )}
              </div>
              
              {/* Active Funding Requests */}
              {fundingRequests.filter(r => r.status === 'open').map(request => (
                <div key={request.request_id} className="mb-4 p-3 bg-black/50 border border-white/[0.08] rounded">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-mono text-sm">
                      ${(request.loan_amount_requested || 0).toLocaleString()}
                    </span>
                    <span className="px-2 py-0.5 bg-[#00FF88]/10 text-[#00FF88] text-[9px] font-bold rounded uppercase">
                      {request.loan_type?.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-3">
                    <Clock className="w-3 h-3" />
                    <span>{request.timeline_days} days</span>
                    <span className="mx-1">•</span>
                    <Users className="w-3 h-3" />
                    <span>{request.interested_lenders?.length || 0} interested</span>
                  </div>
                  
                  {/* Interested Lenders */}
                  {request.interested_lenders_details?.length > 0 && (
                    <div className="space-y-2 mb-3">
                      <p className="text-[10px] text-white/40 uppercase">Lender Bids:</p>
                      {request.interested_lenders_details.map(lender => (
                        <div key={lender.lender_id} className="flex items-center justify-between p-2 bg-white/[0.02] border border-white/[0.06] rounded">
                          <div className="flex-1">
                            <p className="text-white text-xs font-medium">{lender.company_name}</p>
                            {lender.interest_details && (
                              <p className="text-white/50 text-[10px]">
                                {lender.interest_details.estimated_rate}% • {lender.interest_details.estimated_points} pts
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {lender.rating && (
                              <span className="flex items-center gap-0.5 text-gold text-[10px]">
                                <Star className="w-3 h-3 fill-gold" />{lender.rating}
                              </span>
                            )}
                            {request.selected_lender_id !== lender.lender_id ? (
                              <Button
                                size="sm"
                                onClick={() => handleSelectLender(request.request_id, lender.lender_id)}
                                className="h-6 px-2 bg-gold hover:bg-gold/90 text-black text-[10px]"
                              >
                                Select
                              </Button>
                            ) : (
                              <span className="px-2 py-0.5 bg-[#00FF88]/10 text-[#00FF88] text-[9px] font-bold rounded">
                                SELECTED
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {request.interested_lenders?.length === 0 && (
                    <p className="text-white/40 text-xs italic">Waiting for lender responses...</p>
                  )}
                </div>
              ))}
              
              {/* Request Funding Button */}
              {fundingRequests.filter(r => r.status === 'open').length === 0 ? (
                <Button
                  onClick={() => {
                    const capitalNeeded = (deal.asking_price || 0) + (deal.rehab_estimate || 0);
                    setFundingForm({
                      ...fundingForm,
                      loan_amount_requested: capitalNeeded > 0 ? capitalNeeded.toString() : ""
                    });
                    setShowFundingModal(true);
                  }}
                  className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-semibold gap-2"
                  data-testid="request-funding-btn"
                >
                  <Banknote className="w-4 h-4" />
                  Request Capital
                </Button>
              ) : (
                <p className="text-white/50 text-xs text-center">
                  Active request pending. Cancel to create new.
                </p>
              )}
            </div>

            {/* Partner Requests - Compact Combined Panel */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <h3 className="font-heading text-sm font-bold text-white uppercase flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-gold" />
                Partner Requests
              </h3>
              
              <div className="space-y-3">
                {/* Realtor Request */}
                <div className="p-3 bg-black/50 border border-white/[0.06] rounded">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-xs font-medium">Realtor / Listing</span>
                    <span className="text-[9px] text-white/40">For exit strategy</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${BACKEND_URL}/api/deals/${dealId}/listing-request`,
                          { listing_price: deal.arv || deal.exit_price, commission_percent: 3, timeline_days: 90 },
                          { withCredentials: true }
                        );
                        toast.success("Listing request sent to realtor network!");
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to submit request");
                      }
                    }}
                    className="w-full h-7 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs border border-emerald-500/30"
                  >
                    Request Realtor
                  </Button>
                </div>
                
                {/* Title Request */}
                <div className="p-3 bg-black/50 border border-white/[0.06] rounded">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-xs font-medium">Title Company</span>
                    <span className="text-[9px] text-white/40">For closing</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${BACKEND_URL}/api/deals/${dealId}/title-request`,
                          { purchase_price: deal.asking_price, timeline_days: 30 },
                          { withCredentials: true }
                        );
                        toast.success("Title request sent to title company network!");
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to submit request");
                      }
                    }}
                    className="w-full h-7 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-xs border border-blue-500/30"
                  >
                    Request Title
                  </Button>
                </div>
              </div>
            </div>

            {/* AI Autopilot Panel */}
            <AIAutopilotPanel 
              deal={deal} 
              scenarios={scenarios}
              onRefresh={fetchDealData}
            />

            {/* Quick Reference Links */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm font-bold text-white uppercase flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-gold" />
                  Quick Links
                </h3>
                <Button onClick={() => setShowLinkModal(true)} variant="ghost" size="sm" className="text-gold hover:text-gold-light h-7 px-2">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {(!deal.quick_links || deal.quick_links.length === 0) ? (
                <p className="text-white/70 text-xs">No links saved yet.</p>
              ) : (
                <div className="space-y-1">
                  {deal.quick_links.map((link, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-black border border-white/[0.08] group">
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold-light text-xs flex items-center gap-2 truncate flex-1">
                        <LinkIcon className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{link.name}</span>
                      </a>
                      <button onClick={() => handleRemoveQuickLink(idx)} className="text-white/70 hover:text-[#FF3B30] opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Saved Scripts */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <h3 className="font-heading text-sm font-bold text-white uppercase flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-gold" />
                Saved Scripts
              </h3>
              
              {scripts.length === 0 ? (
                <p className="text-white/70 text-xs">No scripts saved. Create them in Settings.</p>
              ) : (
                <div className="space-y-2">
                  {scripts.slice(0, 3).map((script) => (
                    <div key={script.script_id} className="p-2 bg-black border border-white/[0.08]">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium text-xs truncate">{script.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-white/[0.08] text-white/70 flex-shrink-0 ml-2">{script.script_type}</span>
                      </div>
                      <p className="text-[10px] text-white/70 line-clamp-2">
                        {script.content.replace(/\{\{OwnerName\}\}/g, deal.owner_name || "Owner").replace(/\{\{PropertyAddress\}\}/g, deal.property_address)}
                      </p>
                    </div>
                  ))}
                  {scripts.length > 3 && (
                    <Link to="/settings" className="text-xs text-gold hover:text-gold-light block text-center">
                      View all {scripts.length} scripts →
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <h3 className="font-heading text-sm font-bold text-white uppercase flex items-center gap-2 mb-3">
                <Calculator className="w-4 h-4 text-gold" />
                Quick Stats
              </h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-white/70">Scenarios</span>
                  <span className="text-white font-mono">{scenarios.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Notes</span>
                  <span className="text-white font-mono">{activities.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Photos</span>
                  <span className="text-white font-mono">{photos.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Files</span>
                  <span className="text-white font-mono">{files.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scenario Modal */}
        <Dialog open={showScenarioModal} onOpenChange={(open) => { setShowScenarioModal(open); if (!open) resetScenarioForm(); }}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">
                {editingScenario ? "Edit Scenario" : "New Scenario"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCreateScenario} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Scenario Name</Label>
                  <Input value={scenarioForm.name} onChange={(e) => setScenarioForm({...scenarioForm, name: e.target.value})} className="bg-black border-white/[0.08] text-white" placeholder="e.g., Conservative Analysis" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Strategy</Label>
                  <Select value={scenarioForm.strategy} onValueChange={(v) => setScenarioForm({...scenarioForm, strategy: v})}>
                    <SelectTrigger className="bg-black border-white/[0.08] text-white" data-testid="strategy-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                      {strategies.map(s => <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/[0.06]">{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Strategy-specific inputs */}
              {scenarioForm.strategy === "cash" && (
                <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                  <h4 className="text-sm font-bold text-gold uppercase">Cash Purchase</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-white/70">ARV *</Label><Input type="number" value={scenarioForm.arv} onChange={(e) => setScenarioForm({...scenarioForm, arv: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Purchase Price *</Label><Input type="number" value={scenarioForm.purchase_price} onChange={(e) => setScenarioForm({...scenarioForm, purchase_price: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Closing Costs</Label><Input type="number" value={scenarioForm.closing_costs} onChange={(e) => setScenarioForm({...scenarioForm, closing_costs: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Desired Profit</Label><Input type="number" value={scenarioForm.desired_profit} onChange={(e) => setScenarioForm({...scenarioForm, desired_profit: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                  </div>
                </div>
              )}

              {scenarioForm.strategy === "wholesale" && (
                <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                  <h4 className="text-sm font-bold text-gold uppercase">Wholesale</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-white/70">ARV *</Label><Input type="number" value={scenarioForm.arv} onChange={(e) => setScenarioForm({...scenarioForm, arv: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Contract Price *</Label><Input type="number" value={scenarioForm.purchase_price} onChange={(e) => setScenarioForm({...scenarioForm, purchase_price: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Assignment Fee *</Label><Input type="number" value={scenarioForm.assignment_fee} onChange={(e) => setScenarioForm({...scenarioForm, assignment_fee: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Closing Costs</Label><Input type="number" value={scenarioForm.closing_costs} onChange={(e) => setScenarioForm({...scenarioForm, closing_costs: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                  </div>
                </div>
              )}

              {scenarioForm.strategy === "fix_flip" && (
                <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                  <h4 className="text-sm font-bold text-gold uppercase">Fix & Flip</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-white/70">ARV *</Label><Input type="number" value={scenarioForm.arv} onChange={(e) => setScenarioForm({...scenarioForm, arv: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Purchase Price *</Label><Input type="number" value={scenarioForm.purchase_price} onChange={(e) => setScenarioForm({...scenarioForm, purchase_price: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Rehab Estimate</Label><Input type="number" value={scenarioForm.rehab_estimate} onChange={(e) => setScenarioForm({...scenarioForm, rehab_estimate: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Closing Costs</Label><Input type="number" value={scenarioForm.closing_costs} onChange={(e) => setScenarioForm({...scenarioForm, closing_costs: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Holding Costs</Label><Input type="number" value={scenarioForm.holding_costs} onChange={(e) => setScenarioForm({...scenarioForm, holding_costs: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Desired Profit</Label><Input type="number" value={scenarioForm.desired_profit} onChange={(e) => setScenarioForm({...scenarioForm, desired_profit: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                  </div>
                </div>
              )}

              {scenarioForm.strategy === "subject_to" && (
                <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-gold uppercase">Subject-To</h4>
                    <Tooltip><TooltipTrigger><HelpCircle className="w-4 h-4 text-white/70" /></TooltipTrigger>
                      <TooltipContent className="bg-black border-white/[0.08] text-white max-w-xs">
                        <p>Subject-To uses existing loan terms. You're taking over payments on the seller's mortgage.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-white/70">Existing Loan Balance *</Label><Input type="number" value={scenarioForm.existing_loan_balance} onChange={(e) => setScenarioForm({...scenarioForm, existing_loan_balance: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Interest Rate (%) *</Label><Input type="number" step="0.125" value={scenarioForm.existing_interest_rate} onChange={(e) => setScenarioForm({...scenarioForm, existing_interest_rate: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Remaining Term (years) *</Label><Input type="number" value={scenarioForm.remaining_term_years} onChange={(e) => setScenarioForm({...scenarioForm, remaining_term_years: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Monthly PITI</Label><Input type="number" value={scenarioForm.monthly_piti} onChange={(e) => setScenarioForm({...scenarioForm, monthly_piti: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" placeholder="Auto-calc if empty" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Arrears</Label><Input type="number" value={scenarioForm.arrears} onChange={(e) => setScenarioForm({...scenarioForm, arrears: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Cash to Seller</Label><Input type="number" value={scenarioForm.cash_to_seller} onChange={(e) => setScenarioForm({...scenarioForm, cash_to_seller: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Closing Costs</Label><Input type="number" value={scenarioForm.closing_costs} onChange={(e) => setScenarioForm({...scenarioForm, closing_costs: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Hold Period (months)</Label><Input type="number" value={scenarioForm.holding_period_months} onChange={(e) => setScenarioForm({...scenarioForm, holding_period_months: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Monthly Rent (if rental)</Label><Input type="number" value={scenarioForm.monthly_rent} onChange={(e) => setScenarioForm({...scenarioForm, monthly_rent: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Exit Strategy</Label>
                      <Select value={scenarioForm.exit_strategy} onValueChange={(v) => setScenarioForm({...scenarioForm, exit_strategy: v})}>
                        <SelectTrigger className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                          <SelectItem value="keep_rental" className="text-white hover:bg-white/[0.06]">Keep as Rental</SelectItem>
                          <SelectItem value="resell_cash" className="text-white hover:bg-white/[0.06]">Resell (Cash)</SelectItem>
                          <SelectItem value="resell_wrap" className="text-white hover:bg-white/[0.06]">Resell (Wrap)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {scenarioForm.strategy === "seller_finance" && (
                <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-gold uppercase">Seller Finance</h4>
                    <Tooltip><TooltipTrigger><HelpCircle className="w-4 h-4 text-white/70" /></TooltipTrigger>
                      <TooltipContent className="bg-black border-white/[0.08] text-white max-w-xs">
                        <p>The seller acts as the bank. You pay them over time with interest.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-white/70">Purchase Price *</Label><Input type="number" value={scenarioForm.purchase_price} onChange={(e) => setScenarioForm({...scenarioForm, purchase_price: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Down Payment *</Label><Input type="number" value={scenarioForm.down_payment} onChange={(e) => setScenarioForm({...scenarioForm, down_payment: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Interest Rate (%) *</Label><Input type="number" step="0.125" value={scenarioForm.interest_rate} onChange={(e) => setScenarioForm({...scenarioForm, interest_rate: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2"><Label className="text-white/70">Amortization (years) *</Label><Input type="number" value={scenarioForm.amortization_years} onChange={(e) => setScenarioForm({...scenarioForm, amortization_years: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                    <div className="space-y-2">
                      <Label className="text-white/70 flex items-center gap-1">Balloon (years)
                        <Tooltip><TooltipTrigger><HelpCircle className="w-3 h-3 text-white/70" /></TooltipTrigger>
                          <TooltipContent className="bg-black border-white/[0.08] text-white">A balloon is when the remaining balance becomes due. Leave empty for no balloon.</TooltipContent>
                        </Tooltip>
                      </Label>
                      <Input type="number" value={scenarioForm.balloon_years} onChange={(e) => setScenarioForm({...scenarioForm, balloon_years: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" placeholder="0 = no balloon" />
                    </div>
                    <div className="space-y-2"><Label className="text-white/70">Closing Costs</Label><Input type="number" value={scenarioForm.closing_costs} onChange={(e) => setScenarioForm({...scenarioForm, closing_costs: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Hold Period (months)</Label><Input type="number" value={scenarioForm.holding_period_months} onChange={(e) => setScenarioForm({...scenarioForm, holding_period_months: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Monthly Rent</Label><Input type="number" value={scenarioForm.monthly_rent} onChange={(e) => setScenarioForm({...scenarioForm, monthly_rent: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    <div className="space-y-2"><Label className="text-white/70">Exit Price</Label><Input type="number" value={scenarioForm.exit_price} onChange={(e) => setScenarioForm({...scenarioForm, exit_price: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                  </div>
                </div>
              )}

              {scenarioForm.strategy === "wrap" && (
                <div className="space-y-4 p-4 bg-black border border-white/[0.08]">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-gold uppercase">Wrap Mortgage</h4>
                    <Tooltip><TooltipTrigger><HelpCircle className="w-4 h-4 text-white/70" /></TooltipTrigger>
                      <TooltipContent className="bg-black border-white/[0.08] text-white max-w-xs">
                        <p>A wrap "wraps" around the existing loan. Your buyer pays you, and you continue paying the underlying mortgage. The spread is your profit.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  
                  <div className="border-b border-white/[0.08] pb-4">
                    <h5 className="text-xs text-white/70 uppercase mb-3">Underlying Loan</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-white/70">Underlying Balance *</Label><Input type="number" value={scenarioForm.underlying_balance} onChange={(e) => setScenarioForm({...scenarioForm, underlying_balance: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                      <div className="space-y-2"><Label className="text-white/70">Underlying Rate (%) *</Label><Input type="number" step="0.125" value={scenarioForm.underlying_rate} onChange={(e) => setScenarioForm({...scenarioForm, underlying_rate: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                      <div className="space-y-2"><Label className="text-white/70">Remaining Term (years) *</Label><Input type="number" value={scenarioForm.underlying_term_years} onChange={(e) => setScenarioForm({...scenarioForm, underlying_term_years: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                      <div className="space-y-2"><Label className="text-white/70">Monthly PITI (override)</Label><Input type="number" value={scenarioForm.underlying_monthly_piti} onChange={(e) => setScenarioForm({...scenarioForm, underlying_monthly_piti: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" placeholder="Auto-calc if empty" /></div>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-xs text-white/70 uppercase mb-3">Wrap Terms (to Buyer)</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-white/70">Wrap Sale Price *</Label><Input type="number" value={scenarioForm.wrap_sale_price} onChange={(e) => setScenarioForm({...scenarioForm, wrap_sale_price: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                      <div className="space-y-2"><Label className="text-white/70">Buyer Down Payment *</Label><Input type="number" value={scenarioForm.wrap_down_payment} onChange={(e) => setScenarioForm({...scenarioForm, wrap_down_payment: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                      <div className="space-y-2"><Label className="text-white/70">Wrap Rate (%) *</Label><Input type="number" step="0.125" value={scenarioForm.wrap_interest_rate} onChange={(e) => setScenarioForm({...scenarioForm, wrap_interest_rate: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                      <div className="space-y-2"><Label className="text-white/70">Wrap Amort (years) *</Label><Input type="number" value={scenarioForm.wrap_amortization_years} onChange={(e) => setScenarioForm({...scenarioForm, wrap_amortization_years: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" required /></div>
                      <div className="space-y-2"><Label className="text-white/70">Wrap Balloon (years)</Label><Input type="number" value={scenarioForm.wrap_balloon_years} onChange={(e) => setScenarioForm({...scenarioForm, wrap_balloon_years: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" placeholder="0 = no balloon" /></div>
                      <div className="space-y-2"><Label className="text-white/70">Closing Costs</Label><Input type="number" value={scenarioForm.closing_costs} onChange={(e) => setScenarioForm({...scenarioForm, closing_costs: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                      <div className="space-y-2"><Label className="text-white/70">Hold Period (months)</Label><Input type="number" value={scenarioForm.holding_period_months} onChange={(e) => setScenarioForm({...scenarioForm, holding_period_months: e.target.value})} className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white" /></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Real-time Preview */}
              {calcPreview && (
                <div className="p-4 bg-black border border-gold/30">
                  <h4 className="text-sm font-bold text-gold uppercase mb-3">Live Preview</h4>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-xs text-white/70">Profit</p>
                      <p className={`font-mono text-lg font-bold ${calcPreview.estimated_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {formatCurrency(calcPreview.estimated_profit)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-white/70">ROI</p>
                      <p className="font-mono text-lg font-bold text-white">{calcPreview.roi?.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/70">Cash Required</p>
                      <p className="font-mono text-lg font-bold text-white">{formatCurrency(calcPreview.cash_required)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/70">Verdict</p>
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${getVerdictClass(calcPreview.verdict)}`}>{calcPreview.verdict}</span>
                    </div>
                  </div>
                  {calcPreview.monthly_spread !== undefined && (
                    <div className="mt-3 pt-3 border-t border-white/[0.08] text-center">
                      <p className="text-xs text-white/70">Monthly Spread</p>
                      <p className={`font-mono text-2xl font-bold ${calcPreview.monthly_spread >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {formatCurrency(calcPreview.monthly_spread)}
                      </p>
                    </div>
                  )}
                  {calcPreview.risk_flags?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/[0.08]">
                      <div className="flex flex-wrap gap-1">
                        {calcPreview.risk_flags.map((flag, i) => (
                          <span key={`calc-flag-${flag}-${i}`} className="text-xs px-2 py-0.5 bg-red-500/20 text-[#FF3B30]">{flag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowScenarioModal(false)} className="border-white/[0.1] text-white/70 rounded-none">Cancel</Button>
                <Button type="submit" className="gold-gradient-btn rounded-none font-bold" data-testid="save-scenario-btn">
                  {editingScenario ? "Update Scenario" : "Create Scenario"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Note Modal */}
        <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-xl text-white uppercase">Add Note</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateNote} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white/70">Note Type</Label>
                <Select value={noteForm.note_type} onValueChange={(v) => setNoteForm({...noteForm, note_type: v})}>
                  <SelectTrigger className="bg-black border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    {noteTypes.map(t => <SelectItem key={t.value} value={t.value} className="text-white hover:bg-white/[0.06]">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Note</Label>
                <Textarea value={noteForm.content} onChange={(e) => setNoteForm({...noteForm, content: e.target.value})} className="bg-black border-white/[0.08] text-white min-h-[100px]" placeholder="Enter your note..." required />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Contact Outcome (Optional)</Label>
                <Select value={noteForm.contact_outcome} onValueChange={(v) => setNoteForm({...noteForm, contact_outcome: v})}>
                  <SelectTrigger className="bg-black border-white/[0.08] text-white"><SelectValue placeholder="Select outcome..." /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    <SelectItem value="" className="text-white/70">None</SelectItem>
                    {contactOutcomes.map(o => <SelectItem key={o.value} value={o.value} className="text-white hover:bg-white/[0.06]">{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowNoteModal(false)} className="border-white/[0.1] text-white/70 rounded-none">Cancel</Button>
                <Button type="submit" className="gold-gradient-btn rounded-none font-bold" data-testid="save-note-btn">Add Note</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Close/Kill Modal */}
        <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">
                {closeForm.status === "closed" ? "Close Deal - Enter Final Numbers" : "Kill Deal"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Outcome</Label>
                  <Select value={closeForm.status} onValueChange={(v) => setCloseForm({...closeForm, status: v})}>
                    <SelectTrigger className="bg-black border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                      <SelectItem value="closed" className="text-white hover:bg-white/[0.06]">Closed (Won)</SelectItem>
                      <SelectItem value="dead" className="text-white hover:bg-white/[0.06]">Dead (Lost)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Reason</Label>
                  <Select value={closeForm.reason} onValueChange={(v) => setCloseForm({...closeForm, reason: v})}>
                    <SelectTrigger className="bg-black border-white/[0.08] text-white"><SelectValue placeholder="Select reason..." /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                      {closeReasons.map(r => <SelectItem key={r.value} value={r.value} className="text-white hover:bg-white/[0.06]">{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Show detailed closing fields only for won deals */}
              {closeForm.status === "closed" && (
                <>
                  <div className="border-t border-white/[0.08] pt-4 mt-4">
                    <h3 className="text-sm font-bold text-gold uppercase mb-3">Final Numbers</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/70">Actual Purchase Price</Label>
                        <Input 
                          type="number"
                          value={closeForm.actual_purchase_price}
                          onChange={(e) => setCloseForm({...closeForm, actual_purchase_price: e.target.value})}
                          className="bg-black border-white/[0.08] text-white"
                          placeholder={deal.asking_price?.toString() || "0"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Actual Sale Price</Label>
                        <Input 
                          type="number"
                          value={closeForm.actual_sale_price}
                          onChange={(e) => setCloseForm({...closeForm, actual_sale_price: e.target.value})}
                          className="bg-black border-white/[0.08] text-white"
                          placeholder={deal.arv?.toString() || "0"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Actual Rehab Cost</Label>
                        <Input 
                          type="number"
                          value={closeForm.actual_rehab_cost}
                          onChange={(e) => setCloseForm({...closeForm, actual_rehab_cost: e.target.value})}
                          className="bg-black border-white/[0.08] text-white"
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Closing Date</Label>
                        <Input 
                          type="date"
                          value={closeForm.closing_date}
                          onChange={(e) => setCloseForm({...closeForm, closing_date: e.target.value})}
                          className="bg-black border-white/[0.08] text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/[0.08] pt-4">
                    <h3 className="text-sm font-bold text-gold uppercase mb-3">Additional Costs</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/70">Closing Costs</Label>
                        <Input 
                          type="number"
                          value={closeForm.closing_costs}
                          onChange={(e) => setCloseForm({...closeForm, closing_costs: e.target.value})}
                          className="bg-black border-white/[0.08] text-white"
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Holding Costs</Label>
                        <Input 
                          type="number"
                          value={closeForm.holding_costs}
                          onChange={(e) => setCloseForm({...closeForm, holding_costs: e.target.value})}
                          className="bg-black border-white/[0.08] text-white"
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Assignment Fee</Label>
                        <Input 
                          type="number"
                          value={closeForm.assignment_fee}
                          onChange={(e) => setCloseForm({...closeForm, assignment_fee: e.target.value})}
                          className="bg-black border-white/[0.08] text-white"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/[0.08] pt-4">
                    <h3 className="text-sm font-bold text-gold uppercase mb-3">Buyer Info</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/70">Buyer Name</Label>
                        <Input 
                          value={closeForm.buyer_name}
                          onChange={(e) => setCloseForm({...closeForm, buyer_name: e.target.value})}
                          className="bg-black border-white/[0.08] text-white"
                          placeholder="John Smith"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Buyer Type</Label>
                        <Select value={closeForm.buyer_type} onValueChange={(v) => setCloseForm({...closeForm, buyer_type: v})}>
                          <SelectTrigger className="bg-black border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                            <SelectItem value="investor" className="text-white hover:bg-white/[0.06]">Investor</SelectItem>
                            <SelectItem value="owner_occupant" className="text-white hover:bg-white/[0.06]">Owner Occupant</SelectItem>
                            <SelectItem value="wholesaler" className="text-white hover:bg-white/[0.06]">Wholesaler</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Profit Preview */}
                  {closeForm.actual_sale_price && closeForm.actual_purchase_price && (
                    <div className="bg-black border border-white/[0.08] p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-white/70">Estimated Profit</span>
                        <span className={`text-2xl font-mono font-bold ${
                          (parseFloat(closeForm.actual_sale_price) - parseFloat(closeForm.actual_purchase_price) - 
                           parseFloat(closeForm.actual_rehab_cost || 0) - parseFloat(closeForm.closing_costs || 0) - 
                           parseFloat(closeForm.holding_costs || 0)) > 0 
                            ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" : "text-[#FF3B30]"
                        }`}>
                          {formatCurrency(
                            parseFloat(closeForm.actual_sale_price) - parseFloat(closeForm.actual_purchase_price) - 
                            parseFloat(closeForm.actual_rehab_cost || 0) - parseFloat(closeForm.closing_costs || 0) - 
                            parseFloat(closeForm.holding_costs || 0) + parseFloat(closeForm.assignment_fee || 0)
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-white/70">Notes</Label>
                    <Textarea 
                      value={closeForm.notes}
                      onChange={(e) => setCloseForm({...closeForm, notes: e.target.value})}
                      className="bg-black border-white/[0.08] text-white min-h-[60px]"
                      placeholder="Any additional notes about this deal..."
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
                <Button variant="outline" onClick={() => setShowCloseModal(false)} className="border-white/[0.1] text-white/70 rounded-none">Cancel</Button>
                <Button onClick={handleCloseDeal} className={closeForm.status === "dead" ? "bg-red-600 hover:bg-red-700 text-white rounded-none font-bold" : "gold-gradient-btn rounded-none font-bold"}>
                  {closeForm.status === "dead" ? "Kill Deal" : "Close Deal"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Comparable Modal */}
        <Dialog open={showCompModal} onOpenChange={(open) => {
          setShowCompModal(open);
          if (!open) {
            setCompForm({
              address: "", sale_price: "", sale_date: "", beds: "", baths: "",
              sqft: "", distance_miles: "", condition: "similar", notes: "", source_url: ""
            });
            setUrlToParse("");
          }
        }}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">Add Comparable Property</DialogTitle>
            </DialogHeader>
            
            {/* URL Auto-Import Section */}
            <div className="bg-black border border-white/[0.08] rounded p-4 mt-4">
              <div className="flex items-center gap-2 mb-2">
                <LinkIcon className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-white">Auto-Import from URL</span>
              </div>
              <p className="text-xs text-white/50 mb-3">
                Paste a Zillow, Redfin, Realtor.com, or Trulia link to auto-fill property details
              </p>
              <div className="flex gap-2">
                <Input
                  value={urlToParse}
                  onChange={(e) => setUrlToParse(e.target.value)}
                  placeholder="https://www.zillow.com/homedetails/..."
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] text-white text-sm flex-1"
                  data-testid="comp-url-input"
                />
                <Button
                  type="button"
                  onClick={handleParsePropertyUrl}
                  disabled={parsingUrl || !urlToParse.trim()}
                  className="bg-gold hover:bg-gold/90 text-black font-medium"
                >
                  {parsingUrl ? "Parsing..." : "Import"}
                </Button>
              </div>
            </div>
            
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-white/[0.08]" />
              <span className="text-xs text-white/40">OR ENTER MANUALLY</span>
              <div className="flex-1 h-px bg-white/[0.08]" />
            </div>
            
            <form onSubmit={handleAddComparable} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white/70">Property Address *</Label>
                <Input 
                  value={compForm.address} 
                  onChange={(e) => setCompForm({...compForm, address: e.target.value})} 
                  className="bg-black border-white/[0.08] text-white" 
                  placeholder="123 Main St, City, State"
                  required
                  data-testid="comp-address-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Sale Price *</Label>
                  <Input 
                    type="number" 
                    value={compForm.sale_price} 
                    onChange={(e) => setCompForm({...compForm, sale_price: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white" 
                    placeholder="250000"
                    required
                    data-testid="comp-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Sale Date</Label>
                  <Input 
                    type="date" 
                    value={compForm.sale_date} 
                    onChange={(e) => setCompForm({...compForm, sale_date: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Beds</Label>
                  <Input 
                    type="number" 
                    value={compForm.beds} 
                    onChange={(e) => setCompForm({...compForm, beds: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="3"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Baths</Label>
                  <Input 
                    type="number" 
                    step="0.5"
                    value={compForm.baths} 
                    onChange={(e) => setCompForm({...compForm, baths: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="2"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Sqft</Label>
                  <Input 
                    type="number" 
                    value={compForm.sqft} 
                    onChange={(e) => setCompForm({...compForm, sqft: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="1500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Distance (miles)</Label>
                  <Input 
                    type="number" 
                    step="0.1"
                    value={compForm.distance_miles} 
                    onChange={(e) => setCompForm({...compForm, distance_miles: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="0.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Condition vs Subject</Label>
                  <Select 
                    value={compForm.condition} 
                    onValueChange={(v) => setCompForm({...compForm, condition: v})}
                  >
                    <SelectTrigger className="bg-black border-white/[0.08] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                      <SelectItem value="superior" className="text-white hover:bg-white/[0.06]">Superior</SelectItem>
                      <SelectItem value="similar" className="text-white hover:bg-white/[0.06]">Similar</SelectItem>
                      <SelectItem value="inferior" className="text-white hover:bg-white/[0.06]">Inferior</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Notes</Label>
                <Textarea 
                  value={compForm.notes} 
                  onChange={(e) => setCompForm({...compForm, notes: e.target.value})} 
                  className="bg-black border-white/[0.08] text-white min-h-[60px]"
                  placeholder="Recent reno, pool, larger lot, etc."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowCompModal(false)} 
                  className="border-white/[0.1] text-white/70 rounded-none"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="gold-gradient-btn rounded-none font-bold"
                  disabled={savingComp}
                  data-testid="save-comp-btn"
                >
                  {savingComp ? "Adding..." : "Add Comparable"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Quick Link Modal */}
        <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-xl text-white uppercase">Add Quick Link</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white/70">Link Name</Label>
                <Input value={newLink.name} onChange={(e) => setNewLink({...newLink, name: e.target.value})} className="bg-black border-white/[0.08] text-white" placeholder="e.g., Rehab Checklist" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">URL</Label>
                <Input value={newLink.url} onChange={(e) => setNewLink({...newLink, url: e.target.value})} className="bg-black border-white/[0.08] text-white" placeholder="https://..." />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowLinkModal(false)} className="border-white/[0.1] text-white/70 rounded-none">Cancel</Button>
                <Button onClick={handleAddQuickLink} className="gold-gradient-btn rounded-none font-bold">Add Link</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Funding Request Modal */}
        <Dialog open={showFundingModal} onOpenChange={setShowFundingModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase flex items-center gap-2">
                <Banknote className="w-5 h-5 text-gold" />
                Request Capital
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-white/50 text-sm">
                Submit this deal to the lender network. Verified lenders will review and submit bids with their terms.
              </p>
              
              <div className="space-y-2">
                <Label className="text-white/70">Loan Amount Requested</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input 
                    type="number"
                    value={fundingForm.loan_amount_requested} 
                    onChange={(e) => setFundingForm({...fundingForm, loan_amount_requested: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white pl-9 font-mono" 
                    placeholder="150000"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Loan Type</Label>
                <Select 
                  value={fundingForm.loan_type} 
                  onValueChange={(v) => setFundingForm({...fundingForm, loan_type: v})}
                >
                  <SelectTrigger className="bg-black border-white/[0.08] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    <SelectItem value="hard_money" className="text-white hover:bg-white/[0.06]">Hard Money</SelectItem>
                    <SelectItem value="bridge_loan" className="text-white hover:bg-white/[0.06]">Bridge Loan</SelectItem>
                    <SelectItem value="dscr_loan" className="text-white hover:bg-white/[0.06]">DSCR Loan</SelectItem>
                    <SelectItem value="private_money" className="text-white hover:bg-white/[0.06]">Private Money</SelectItem>
                    <SelectItem value="fix_flip_loan" className="text-white hover:bg-white/[0.06]">Fix & Flip Loan</SelectItem>
                    <SelectItem value="construction_loan" className="text-white hover:bg-white/[0.06]">Construction Loan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Timeline (days to close)</Label>
                <Select 
                  value={fundingForm.timeline_days.toString()} 
                  onValueChange={(v) => setFundingForm({...fundingForm, timeline_days: parseInt(v)})}
                >
                  <SelectTrigger className="bg-black border-white/[0.08] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    <SelectItem value="14" className="text-white hover:bg-white/[0.06]">14 days (Rush)</SelectItem>
                    <SelectItem value="21" className="text-white hover:bg-white/[0.06]">21 days</SelectItem>
                    <SelectItem value="30" className="text-white hover:bg-white/[0.06]">30 days (Standard)</SelectItem>
                    <SelectItem value="45" className="text-white hover:bg-white/[0.06]">45 days</SelectItem>
                    <SelectItem value="60" className="text-white hover:bg-white/[0.06]">60 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Additional Notes (optional)</Label>
                <Textarea 
                  value={fundingForm.notes} 
                  onChange={(e) => setFundingForm({...fundingForm, notes: e.target.value})} 
                  className="bg-black border-white/[0.08] text-white min-h-[80px]"
                  placeholder="Exit strategy, experience level, rehab scope, etc."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowFundingModal(false)} 
                  className="border-white/[0.1] text-white/70 rounded-none"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitFundingRequest}
                  className="bg-gold hover:bg-gold/90 text-black font-bold rounded-none"
                  disabled={submittingFunding}
                >
                  {submittingFunding ? "Submitting..." : "Submit to Lenders"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>

    {/* Post-Close Partner Review Nudge */}
    {showReviewNudge && deal && (
      <PostCloseReviewModal
        dealId={deal.deal_id}
        dealAddress={deal.property_address}
        onClose={() => setShowReviewNudge(false)}
      />
    )}
    </TooltipProvider>
  );
}
